import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { couldNotReach, authCouldNotReach } from '../utils/reachability';

/**
 * Custom hook to get current user's role and permissions
 * @returns {Object} { role, status, loading, error, unreachable, user, isClient, isCoach, isAdmin, isSuperAdmin, canAccessAdmin, refetch }
 *
 * When the server does not answer, the role already known is KEPT and `unreachable` is true. A
 * coach does not stop being a coach because the database was slow; before this, every failed
 * re-check (there is one each time the token is renewed or the app comes back to the front)
 * blanked the role, and the coach saw "Access Denied" in place of the screen they were working in.
 */
export const useUserRole = () => {
  const [role, setRole] = useState(null);
  const [status, setStatus] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreachable, setUnreachable] = useState(false);

  const fetchUserRole = async (authUser = null) => {
    // The server did not answer: that says nothing about who this person is. Keep what was known.
    const keepWhatWasKnown = (err) => {
      console.error('Could not reach the server for the user role:', err);
      setError(err);
      setUnreachable(true);
      setLoading(false);
    };

    try {
      setLoading(true);
      setError(null);

      // authUser is passed directly from onAuthStateChange to avoid calling
      // getSession() inside the callback — getSession() acquires _acquireLock(-1)
      // which deadlocks if setSession() is still holding the same lock (cross-app switch).
      if (!authUser) {
        const { data, error: userError } = await supabase.auth.getUser();
        if (authCouldNotReach(userError)) {
          keepWhatWasKnown(userError);
          return;
        }
        if (userError || !data?.user) {
          setUnreachable(false);
          setRole(null);
          setStatus(null);
          setUser(null);
          setLoading(false);
          return;
        }
        authUser = data.user;
      }

      // Get user profile with role and status
      const { data: userData, error: userError, status: answered } = await supabase
        .from('users')
        .select('id, auth_user_id, name, email, role, status, coach_id, approved_at')
        .eq('auth_user_id', authUser.id)
        .single();

      if (userError) {
        if (couldNotReach({ error: userError, status: answered })) {
          keepWhatWasKnown(userError);
          return;
        }
        setUnreachable(false);
        if (userError.code === 'PGRST116') {
          // No rows — new user not yet in the users table (expected during signup)
          setRole('client');
          setStatus('pending');
        } else {
          // Real DB error — don't grant any role; show error state
          console.error('User profile fetch failed:', userError);
          setError(userError);
          setRole(null);
          setStatus(null);
        }
        setUser(null);
        setLoading(false);
        return;
      }

      setRole(userData?.role || 'client');
      setStatus(userData?.status || 'pending');
      setUser(userData);
      setUnreachable(false);
      setLoading(false);
    } catch (err) {
      if (authCouldNotReach(err)) {
        keepWhatWasKnown(err);
        return;
      }
      setUnreachable(false);
      // Only log actual errors, not missing session errors
      if (err.name !== 'AuthSessionMissingError') {
        console.error('Error fetching user role:', err);
        setError(err);
      }
      // Set everything to null/default state
      setRole(null);
      setStatus(null);
      setUser(null);
      setLoading(false);
    }
  };

  // Nobody is signed in any more. Also called by hand after a log-out the server never heard of
  // (src/utils/signOut.js), when the library sends no SIGNED_OUT.
  const forget = () => {
    setUnreachable(false);
    setRole(null);
    setStatus(null);
    setUser(null);
    setLoading(false);
  };

  useEffect(() => {
    // Listen for auth state changes — INITIAL_SESSION fires on mount,
    // so we don't need a separate fetchUserRole() call on mount.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        fetchUserRole(session?.user ?? null);
      } else if (event === 'SIGNED_OUT') {
        forget();
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Helper booleans for role checking
  const isClient = role === 'client';
  const isCoach = role === 'coach' || role === 'admin' || role === 'super_admin';
  const isAdmin = role === 'admin' || role === 'super_admin';
  const isSuperAdmin = role === 'super_admin';

  // Check if user can access admin panel
  const canAccessAdmin = (role === 'coach' || role === 'admin' || role === 'super_admin') && status === 'active';

  // Check if user account is active
  const isActive = status === 'active';
  const isPending = status === 'pending';
  const isSuspended = status === 'suspended';
  const isRejected = status === 'rejected';

  return {
    role,
    status,
    user,
    loading,
    error,
    unreachable,
    // Role helpers
    isClient,
    isCoach,
    isAdmin,
    isSuperAdmin,
    canAccessAdmin,
    // Status helpers
    isActive,
    isPending,
    isSuspended,
    isRejected,
    // Refetch function (useful after approvals)
    refetch: fetchUserRole,
    forget
  };
};

/**
 * Check if user has permission for a specific action
 * @param {string} userRole - Current user's role
 * @param {string} requiredRole - Minimum required role
 * @returns {boolean} - True if user has permission
 */
export const hasPermission = (userRole, requiredRole) => {
  const roleHierarchy = {
    'super_admin': 4,
    'admin': 3,
    'coach': 2,
    'client': 1
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
};

/**
 * Role constants for easy reference
 */
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  COACH: 'coach',
  CLIENT: 'client'
};

/**
 * Status constants
 */
export const STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  REJECTED: 'rejected'
};
